import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { CreatePlan, CreatePlanRequest } from "../Services/APIService";

interface CreatePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

interface FormData {
  description: string;
  amount: string;
  allowedNoOfUser: string;
  allowedNoOfLeads: string;
  allowedSms: string;
  allowedNoOfEmail: string;
  validity: string;
}

interface ValidationErrors {
  description?: string;
  amount?: string;
  allowedNoOfUser?: string;
  allowedNoOfLeads?: string;
  allowedSms?: string;
  allowedNoOfEmail?: string;
  validity?: string;
}

const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormData>({
    description: "",
    amount: "",
    allowedNoOfUser: "",
    allowedNoOfLeads: "",
    allowedSms: "",
    allowedNoOfEmail: "",
    validity: "",
  });
  const navigate = useNavigate();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // For numeric fields, allow only numbers
    if (
      [
        "amount",
        "allowedNoOfUser",
        "allowedNoOfLeads",
        "allowedSms",
        "allowedNoOfEmail",
        "validity",
      ].includes(name)
    ) {
      if (value && !/^\d*$/.test(value)) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (value && errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const getLocalStorageValue = (key: string): number => {
    const value = localStorage.getItem(key);
    return value ? parseInt(value, 10) : 0;
  };

  const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    }
    if (!formData.amount || parseInt(formData.amount) <= 0) {
      newErrors.amount = "Valid amount is required";
      isValid = false;
    }
    if (!formData.allowedNoOfUser || parseInt(formData.allowedNoOfUser) < 0) {
      newErrors.allowedNoOfUser = "Number of users is required";
      isValid = false;
    }
    if (!formData.allowedNoOfLeads || parseInt(formData.allowedNoOfLeads) < 0) {
      newErrors.allowedNoOfLeads = "Number of leads is required";
      isValid = false;
    }
    if (!formData.allowedSms || parseInt(formData.allowedSms) < 0) {
      newErrors.allowedSms = "Number of SMS is required";
      isValid = false;
    }
    if (!formData.allowedNoOfEmail || parseInt(formData.allowedNoOfEmail) < 0) {
      newErrors.allowedNoOfEmail = "Number of emails is required";
      isValid = false;
    }
    if (!formData.validity || parseInt(formData.validity) <= 0) {
      newErrors.validity = "Valid validity period is required";
      isValid = false;
    }

    return { isValid, errors: newErrors };
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const validation = validateForm();
      if (!validation.isValid) {
        setErrors(validation.errors);
        setIsSubmitting(false);
        return;
      }

      const createdBy = getLocalStorageValue("superAdminId");

      const requestData: CreatePlanRequest = {
        description: formData.description,
        amount: parseInt(formData.amount),
        allowedNoOfUser: parseInt(formData.allowedNoOfUser),
        allowedNoOfLeads: parseInt(formData.allowedNoOfLeads),
        allowedSms: parseInt(formData.allowedSms),
        allowedNoOfEmail: parseInt(formData.allowedNoOfEmail),
        validity: parseInt(formData.validity),
        createdBy: createdBy,
        modifiedBy: createdBy,
        modifiedOn: new Date().toISOString(),
        createdOn: new Date().toISOString(),
      };

      const result = await CreatePlan(requestData);

      toast.success("Plan created successfully!");

      setFormData({
        description: "",
        amount: "",
        allowedNoOfUser: "",
        allowedNoOfLeads: "",
        allowedSms: "",
        allowedNoOfEmail: "",
        validity: "",
      });

      onSubmit();
      onClose();
    } catch (error) {
      console.error("Error creating plan:", error);
      toast.error("Error creating plan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      description: "",
      amount: "",
      allowedNoOfUser: "",
      allowedNoOfLeads: "",
      allowedSms: "",
      allowedNoOfEmail: "",
      validity: "",
    });
    setErrors({});
    onClose();
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          zIndex: 10000,
        }}
      />
      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content usertaskCreatFormContent">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Create New Plan</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="card-body p-0 overflow-hidden">
                <div className="modal-body-content">
                  <form>
                    <div className="row">
                      <div className="col-12 mb-3 input-group-custom">
                        <label htmlFor="description" className="form-label">
                          Description{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-note"></span>
                          <textarea
                            onChange={handleInputChange}
                            id="description"
                            name="description"
                            className={`form-control-custom-textarea ${
                              errors.description ? "is-invalid" : ""
                            }`}
                            placeholder="Enter plan description"
                            value={formData.description}
                            rows={3}
                          />
                          {errors.description && (
                            <div className="invalid-feedback-custom">
                              {errors.description}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="amount" className="form-label">
                          Amount (₹){requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="amount"
                            name="amount"
                            className={`form-control-custom form-control-custom1 ${
                              errors.amount ? "is-invalid" : ""
                            }`}
                            placeholder="Enter amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                          />
                          {errors.amount && (
                            <div className="invalid-feedback-custom">
                              {errors.amount}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="validity" className="form-label">
                          Validity (Days){requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="validity"
                            name="validity"
                            className={`form-control-custom form-control-custom1 ${
                              errors.validity ? "is-invalid" : ""
                            }`}
                            placeholder="Enter validity in days"
                            value={formData.validity}
                            onChange={handleInputChange}
                          />
                          {errors.validity && (
                            <div className="invalid-feedback-custom">
                              {errors.validity}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="allowedNoOfUser" className="form-label">
                          Allowed No. of Users{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="allowedNoOfUser"
                            name="allowedNoOfUser"
                            className={`form-control-custom form-control-custom1 ${
                              errors.allowedNoOfUser ? "is-invalid" : ""
                            }`}
                            placeholder="Enter number of users"
                            value={formData.allowedNoOfUser}
                            onChange={handleInputChange}
                          />
                          {errors.allowedNoOfUser && (
                            <div className="invalid-feedback-custom">
                              {errors.allowedNoOfUser}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label
                          htmlFor="allowedNoOfLeads"
                          className="form-label"
                        >
                          Allowed No. of Leads{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="allowedNoOfLeads"
                            name="allowedNoOfLeads"
                            className={`form-control-custom form-control-custom1 ${
                              errors.allowedNoOfLeads ? "is-invalid" : ""
                            }`}
                            placeholder="Enter number of leads"
                            value={formData.allowedNoOfLeads}
                            onChange={handleInputChange}
                          />
                          {errors.allowedNoOfLeads && (
                            <div className="invalid-feedback-custom">
                              {errors.allowedNoOfLeads}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label htmlFor="allowedSms" className="form-label">
                          Allowed SMS{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="allowedSms"
                            name="allowedSms"
                            className={`form-control-custom form-control-custom1 ${
                              errors.allowedSms ? "is-invalid" : ""
                            }`}
                            placeholder="Enter number of SMS"
                            value={formData.allowedSms}
                            onChange={handleInputChange}
                          />
                          {errors.allowedSms && (
                            <div className="invalid-feedback-custom">
                              {errors.allowedSms}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label
                          htmlFor="allowedNoOfEmail"
                          className="form-label"
                        >
                          Allowed No. of Emails{requiredField}
                        </label>
                        <div className="input-with-icon">
                          <input
                            type="text"
                            id="allowedNoOfEmail"
                            name="allowedNoOfEmail"
                            className={`form-control-custom form-control-custom1 ${
                              errors.allowedNoOfEmail ? "is-invalid" : ""
                            }`}
                            placeholder="Enter number of emails"
                            value={formData.allowedNoOfEmail}
                            onChange={handleInputChange}
                          />
                          {errors.allowedNoOfEmail && (
                            <div className="invalid-feedback-custom">
                              {errors.allowedNoOfEmail}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="btn-custom btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-custom btn-primary-custom"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Plan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreatePlanModal;
